import type { OfferDeliverChannel, WorkOrderOffer, WorkOrderOfferLine } from '@/hooks/useWorkOrderOffers';
import {
  OFFER_DELIVER_CHANNELS,
  isNonTerminalOfferStatus,
  isPendingOfferStatus,
} from '@/hooks/useWorkOrderOffers';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';

/** `sentVia` is a free string on the wire; only channels the portal can send are localized. */
export function isOfferDeliverChannel(value: string): value is OfferDeliverChannel {
  return (OFFER_DELIVER_CHANNELS as readonly string[]).includes(value);
}

export function lineGrossCents(line: WorkOrderOfferLine): number {
  return line.totalGrossCents ?? 0;
}

export function offerMoneyBuckets(offers: readonly WorkOrderOffer[]): {
  agreed: number;
  awaiting: number;
  declined: number;
} {
  let agreed = 0;
  let awaiting = 0;
  let declined = 0;
  for (const offer of offers) {
    for (const line of offer.lines) {
      const cents = lineGrossCents(line);
      if (line.decision === 'approved') {
        agreed += cents;
      } else if (line.decision === 'declined') {
        declined += cents;
      } else if (line.decision === 'pending' && isPendingOfferStatus(offer.status)) {
        awaiting += cents;
      }
    }
  }
  return { agreed, awaiting, declined };
}

export function sortOffersNewestFirst(offers: readonly WorkOrderOffer[]): WorkOrderOffer[] {
  return [...offers].sort((a, b) => {
    if (b.sequenceNo !== a.sequenceNo) return b.sequenceNo - a.sequenceNo;
    return b.id.localeCompare(a.id);
  });
}

export function itemIdsOnNonTerminalOffers(offers: readonly WorkOrderOffer[]): Set<string> {
  const ids = new Set<string>();
  for (const offer of offers) {
    if (!isNonTerminalOfferStatus(offer.status)) continue;
    for (const line of offer.lines) {
      ids.add(line.workOrderItemId);
    }
  }
  return ids;
}

export function eligibleOfferItems(
  items: WorkOrderDetail['items'],
  offers: readonly WorkOrderOffer[],
): WorkOrderDetail['items'] {
  const taken = itemIdsOnNonTerminalOffers(offers);
  return items.filter((item) => item.status === 'pending' && !taken.has(item.id));
}

export function hasPendingOffer(offers: readonly WorkOrderOffer[]): boolean {
  return offers.some((offer) => isPendingOfferStatus(offer.status));
}

export function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
